import { useMemo, useRef, useState } from "react";
import { errorMessage } from "../api/client";
import { useCreateClips, useRecordingClips } from "../api/clips";
import { useClipSuggestions } from "../api/clipSuggestions";
import { useTasks } from "../api/tasks";
import type { ClipSuggestionRead, RecordingDetail, TaskRead } from "../api/types";
import { categoryColor } from "../lib/grade";
import ClipSuggestionsPanel from "./ClipSuggestionsPanel";
import ClipTimeline, {
  DRAG_MIN_MS,
  type BlockedRange,
  type ClipRange,
  type IdleLaneItem,
  type SuggestionLaneItem,
} from "./ClipTimeline";
import ConfirmDialog from "./ConfirmDialog";

// Clipping mode for the recording detail page. The reviewer marks N ranges on the trim
// track (each becomes one deliverable clip), assigns each the task performed in it, and
// confirms — one Clip row + worker job per range. The buyer's clip definition stays
// visible the whole time; ranges under the 5-minute floor block the confirm. Ranges can
// never overlap — not each other and not the recording's already-created clips (shown as
// gray blocks on the track); the timeline clamps drags and this component only offers
// free slots when adding.

// The confirm floor mirrors the server's CLIP_MIN_DURATION_MS. Override in dev builds
// (.env.local: VITE_CLIP_MIN_MS=10000) when testing with short seeded recordings.
const CLIP_MIN_MS = Number(import.meta.env.VITE_CLIP_MIN_MS ?? 300_000);

export const CLARU_CLIP_DEFINITION =
  "One activity per clip, at least 5 minutes, filmed start to finish. No cuts mid-task, " +
  "no edits, no speed changes, no idle gap over 10 seconds.";

interface Props {
  rec: RecordingDetail;
  videoRef: React.RefObject<HTMLVideoElement>;
  onClose: () => void;
}

function fmt(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function groupByCategory(tasks: TaskRead[]): [string, TaskRead[]][] {
  const groups = new Map<string, TaskRead[]>();
  for (const t of tasks) {
    const list = groups.get(t.categoryEn) ?? [];
    list.push(t);
    groups.set(t.categoryEn, list);
  }
  return [...groups.entries()];
}

/** Free intervals of the timeline once every occupied window is carved out. */
function freeGaps(durationMs: number, occupied: BlockedRange[]): BlockedRange[] {
  const sorted = [...occupied].sort((a, b) => a.startMs - b.startMs);
  const gaps: BlockedRange[] = [];
  let cursor = 0;
  for (const o of sorted) {
    if (o.startMs > cursor) gaps.push({ startMs: cursor, endMs: o.startMs });
    cursor = Math.max(cursor, o.endMs);
  }
  if (cursor < durationMs) gaps.push({ startMs: cursor, endMs: durationMs });
  return gaps.filter((g) => g.endMs - g.startMs >= DRAG_MIN_MS);
}

function intersects(a: BlockedRange, b: BlockedRange): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

export default function ClipComposer({ rec, videoRef, onClose }: Props) {
  const durationMs = rec.duration_ms ?? 0;
  const tasks = useTasks();
  const existing = useRecordingClips(rec.id);
  const create = useCreateClips(rec.id);
  const suggestions = useClipSuggestions(rec.id);
  const [ranges, setRanges] = useState<ClipRange[]>([]);
  const [selectedKey, setSelectedKey] = useState<number | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const nextKey = useRef(1);

  const taskGroups = useMemo(() => groupByCategory(tasks.data ?? []), [tasks.data]);
  const knownTaskIds = useMemo(() => new Set((tasks.data ?? []).map((t) => t.id)), [tasks.data]);

  // windows already claimed by this recording's clips (snapped when the job ran, else
  // requested) — immovable obstacles on the track and in validation
  const blocked = useMemo<BlockedRange[]>(
    () =>
      (existing.data?.items ?? []).map((c) => ({
        startMs: c.snapped_start_ms ?? c.start_ms,
        endMs: c.snapped_end_ms ?? c.end_ms,
      })),
    [existing.data],
  );

  const gaps = useMemo(
    () => freeGaps(durationMs, [...blocked, ...ranges]),
    [durationMs, blocked, ranges],
  );

  // one clip spanning the entire recording — only meaningful while nothing else claims
  // any window (a full-span range would overlap anything that exists)
  const canUseWholeRecording =
    durationMs > 0 && ranges.length === 0 && blocked.length === 0;
  const addWholeRecording = () => {
    if (!canUseWholeRecording) return;
    const declared = rec.task?.id && knownTaskIds.has(rec.task.id) ? rec.task.id : "";
    const key = nextKey.current++;
    setRanges([{ key, startMs: 0, endMs: durationMs, taskId: declared }]);
    setSelectedKey(key);
  };

  const addAtPlayhead = () => {
    const atMs = Math.round((videoRef.current?.currentTime ?? 0) * 1000);
    // place in the free gap under the playhead; else the next one; else the last one left
    const gap =
      gaps.find((g) => atMs >= g.startMs && atMs < g.endMs) ??
      gaps.find((g) => g.startMs >= atMs) ??
      gaps[gaps.length - 1];
    if (!gap) return;
    const start = Math.max(gap.startMs, Math.min(atMs, gap.endMs - DRAG_MIN_MS));
    const end = Math.min(gap.endMs, start + CLIP_MIN_MS);
    // default to the task the uploader declared for the recording, when it's in the catalog
    const declared = rec.task?.id && knownTaskIds.has(rec.task.id) ? rec.task.id : "";
    const key = nextKey.current++;
    setRanges((rs) => [...rs, { key, startMs: start, endMs: end, taskId: declared }]);
    setSelectedKey(key);
  };

  const seek = (ms: number) => {
    const v = videoRef.current;
    if (v) v.currentTime = ms / 1000;
  };

  /** Turn an AI suggestion into an editable range, clamped to the free room around it.
   * ``extendToMin`` stretches a short proposal to the 5-minute floor (forward first, then
   * backwards) so it can be delivered. Returns false when no free gap can hold it. */
  const addFromSuggestion = (s: ClipSuggestionRead, extendToMin: boolean): boolean => {
    if (ranges.some((r) => r.suggestionId === s.id)) return true;
    const overlapping = gaps
      .map((g) => ({ g, len: Math.min(g.endMs, s.end_ms) - Math.max(g.startMs, s.start_ms) }))
      .filter((x) => x.len >= DRAG_MIN_MS)
      .sort((a, b) => b.len - a.len)[0];
    if (!overlapping) return false;
    const gap = overlapping.g;
    let start = Math.max(gap.startMs, s.start_ms);
    let end = Math.min(gap.endMs, s.end_ms);
    if (extendToMin && end - start < CLIP_MIN_MS) {
      end = Math.min(gap.endMs, start + CLIP_MIN_MS);
      if (end - start < CLIP_MIN_MS) start = Math.max(gap.startMs, end - CLIP_MIN_MS);
    }
    const taskId = s.task_id && knownTaskIds.has(s.task_id) ? s.task_id : "";
    const declared = rec.task?.id && knownTaskIds.has(rec.task.id) ? rec.task.id : "";
    const key = nextKey.current++;
    setRanges((rs) =>
      [
        ...rs,
        { key, startMs: start, endMs: end, taskId: taskId || declared, suggestionId: s.id },
      ].sort((a, b) => a.startMs - b.startMs),
    );
    setSelectedKey(key);
    setSelectedSuggestion(s.id);
    seek(start);
    return true;
  };

  const addAllSuggestions = () => {
    // sequential adds must see each other's window: fold through a local copy of the gaps
    let occupied: BlockedRange[] = [...blocked, ...ranges];
    const next: ClipRange[] = [];
    for (const s of suggestions.data?.items ?? []) {
      if (s.status !== "proposed" || s.too_short) continue;
      if (ranges.some((r) => r.suggestionId === s.id)) continue;
      const free = freeGaps(durationMs, occupied);
      const gap = free.find(
        (g) => Math.min(g.endMs, s.end_ms) - Math.max(g.startMs, s.start_ms) >= CLIP_MIN_MS,
      );
      if (!gap) continue;
      const start = Math.max(gap.startMs, s.start_ms);
      const end = Math.min(gap.endMs, s.end_ms);
      const taskId = s.task_id && knownTaskIds.has(s.task_id) ? s.task_id : "";
      next.push({ key: nextKey.current++, startMs: start, endMs: end, taskId, suggestionId: s.id });
      occupied = [...occupied, { startMs: start, endMs: end }];
    }
    if (next.length === 0) return;
    setRanges((rs) => [...rs, ...next].sort((a, b) => a.startMs - b.startMs));
    setSelectedKey(next[0].key);
  };

  const addedSuggestions = useMemo(() => {
    const m = new Map<number, number>();
    ranges.forEach((r, i) => {
      if (r.suggestionId != null) m.set(r.suggestionId, i + 1);
    });
    return m;
  }, [ranges]);

  const laneItems = useMemo<SuggestionLaneItem[]>(
    () =>
      (suggestions.data?.items ?? [])
        .filter((s) => s.status === "proposed")
        .map((s) => ({
          id: s.id,
          startMs: s.start_ms,
          endMs: s.end_ms,
          label: `${s.task_title ?? "?"} · ${Math.round(s.confidence * 100)}%`,
          color: categoryColor(s.task_category_id),
          confidence: s.confidence,
          tooShort: s.too_short,
          added: addedSuggestions.has(s.id),
        })),
    [suggestions.data, addedSuggestions],
  );
  const idleGaps = useMemo<IdleLaneItem[]>(
    () =>
      (suggestions.data?.recommendation?.idle_gaps ?? []).map((g) => ({
        startMs: g.start_ms,
        endMs: g.end_ms,
        source: g.source,
        reason: g.reason,
      })),
    [suggestions.data],
  );

  const update = (key: number, patch: Partial<ClipRange>) =>
    setRanges((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key: number) => {
    setRanges((rs) => rs.filter((r) => r.key !== key));
    if (selectedKey === key) setSelectedKey(null);
  };

  const problems = ranges.map((r) => {
    if (r.endMs - r.startMs < CLIP_MIN_MS) return "under 5 minutes";
    if (!r.taskId) return "no task selected";
    return null;
  });
  // the timeline clamps drags so this should never trip — defensive gate all the same
  const hasOverlap = ranges.some(
    (r, i) =>
      ranges.some((o, j) => j > i && intersects(r, o)) || blocked.some((b) => intersects(r, b)),
  );
  const canConfirm =
    ranges.length > 0 && problems.every((p) => p == null) && !hasOverlap && !create.isPending;

  const confirm = () => {
    create.mutate(
      {
        clips: ranges.map((r) => ({
          start_ms: r.startMs,
          end_ms: r.endMs,
          task_id: r.taskId,
          suggestion_id: r.suggestionId ?? null,
        })),
      },
      {
        onSuccess: () => {
          setConfirming(false);
          onClose();
        },
        onError: () => setConfirming(false),
      },
    );
  };

  return (
    <section className="card clip-composer">
      <div className="card-head">
        <h3>Create clips</h3>
        <button className="btn small ghost" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="claru-note">
        <b>What counts as a clip.</b> {CLARU_CLIP_DEFINITION}
        <span className="muted">
          {" "}
          Clips cannot overlap. The clip start snaps back to the nearest keyframe (up to 1s
          earlier) so the video is delivered without re-encoding.
        </span>
      </div>

      <ClipTimeline
        durationMs={durationMs}
        ranges={ranges}
        blocked={blocked}
        selectedKey={selectedKey}
        minMs={CLIP_MIN_MS}
        videoRef={videoRef}
        onSelect={setSelectedKey}
        onChange={(key, startMs, endMs) => update(key, { startMs, endMs })}
        suggestions={laneItems}
        idleGaps={idleGaps}
        selectedSuggestionId={selectedSuggestion}
        onPickSuggestion={setSelectedSuggestion}
      />

      {ranges.length === 0 && (
        <p className="muted small clip-empty-hint">
          {laneItems.length > 0
            ? "Add an AI suggestion below, or seek the video to where an activity starts and add a clip."
            : "Seek the video to where an activity starts, then add a clip and drag its handles."}
          {blocked.length > 0 && " Gray blocks are windows already delivered as clips."}
        </p>
      )}

      <div className="clip-rows">
        {ranges.map((r, idx) => {
          const dur = r.endMs - r.startMs;
          const short = dur < CLIP_MIN_MS;
          return (
            <div
              key={r.key}
              className={r.key === selectedKey ? "clip-row selected" : "clip-row"}
              onClick={() => setSelectedKey(r.key)}
            >
              <span className="clip-row-index">{idx + 1}</span>
              <span className="clip-row-window mono">
                {fmt(r.startMs)}–{fmt(r.endMs)}
              </span>
              {r.suggestionId != null && (
                <span className="ai-tag" title="From an AI suggestion — trim and pick the task as usual">
                  ✦ AI
                </span>
              )}
              <span
                className={short ? "clip-row-dur bad" : "clip-row-dur"}
                title={short ? "Claru requires at least 5 minutes per clip" : undefined}
              >
                {fmt(dur)}
                {short && " · under 5:00"}
              </span>
              <select
                className="clip-row-task"
                value={r.taskId}
                onChange={(e) => update(r.key, { taskId: e.target.value })}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="" disabled>
                  Select the task performed…
                </option>
                {taskGroups.map(([category, items]) => (
                  <optgroup key={category} label={category}>
                    {items.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.titleEn}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <button
                className="btn small ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(r.key);
                }}
                title="Remove this clip"
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      {hasOverlap && <p className="error">Clips cannot overlap. Adjust the windows.</p>}
      {create.error != null && <p className="error">{errorMessage(create.error)}</p>}

      <ClipSuggestionsPanel
        recordingId={rec.id}
        durationMs={durationMs}
        data={suggestions.data}
        loading={suggestions.isLoading}
        minMs={CLIP_MIN_MS}
        added={addedSuggestions}
        selectedId={selectedSuggestion}
        onSelect={setSelectedSuggestion}
        onSeek={seek}
        onAdd={addFromSuggestion}
        onAddAll={addAllSuggestions}
      />

      <div className="clip-actions">
        <button
          className="btn clip-whole-rec"
          onClick={addWholeRecording}
          disabled={!canUseWholeRecording}
          title={
            canUseWholeRecording
              ? "One clip spanning the entire recording; pick its task and confirm"
              : "Available only while no other clip or range claims part of this recording"
          }
        >
          Create clip from original recording without cutting
        </button>
        <button
          className="btn"
          onClick={addAtPlayhead}
          disabled={durationMs <= 0 || gaps.length === 0}
          title={
            gaps.length === 0
              ? "No free room left on the timeline for another clip"
              : "Add a clip starting at the playhead"
          }
        >
          + Add clip at playhead
        </button>
        <button
          className="btn primary"
          disabled={!canConfirm}
          onClick={() => setConfirming(true)}
          title={
            canConfirm
              ? undefined
              : "Every clip needs a task, at least 5 minutes, and no overlaps before confirming"
          }
        >
          Create {ranges.length || ""} clip{ranges.length === 1 ? "" : "s"}
        </button>
      </div>

      <ConfirmDialog
        open={confirming}
        title={`Create ${ranges.length} clip${ranges.length === 1 ? "" : "s"}?`}
        confirmLabel={create.isPending ? "Creating…" : "Create clips"}
        busy={create.isPending}
        onConfirm={confirm}
        onCancel={() => setConfirming(false)}
      >
        <p>
          The worker will cut each window losslessly from the original video and package it with
          its IMU and frame data. Ready clips appear on the Clips page for download.
        </p>
        <ul className="clip-confirm-list">
          {ranges.map((r, idx) => {
            const task = tasks.data?.find((t) => t.id === r.taskId);
            return (
              <li key={r.key}>
                Clip {idx + 1}: {fmt(r.startMs)}–{fmt(r.endMs)} · {task?.titleEn ?? r.taskId}
              </li>
            );
          })}
        </ul>
      </ConfirmDialog>
    </section>
  );
}
